'use client';

import * as React from 'react';
import { CheckIcon, CopyIcon } from 'lucide-react';

import { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';

interface CopyableCodeBlockProps extends React.HTMLAttributes<HTMLElement> {
    children?: React.ReactNode;
    className?: string;
    'data-language'?: string;
}

export function CopyableCodeBlock({
    children,
    className,
    'data-language': language,
    ...props
}: CopyableCodeBlockProps): React.JSX.Element {
    const [copied, setCopied] = React.useState(false);
    const preRef = React.useRef<HTMLPreElement>(null);

    const handleCopy = React.useCallback(() => {
        if (preRef.current) {
            // Get the text content from the pre element
            const codeElement = preRef.current.querySelector('code');
            const textToCopy = codeElement?.textContent || preRef.current.textContent || '';

            navigator.clipboard.writeText(textToCopy).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            });
        }
    }, []);

    return (
        <div className="relative group">
            <pre
                ref={preRef}
                className={cn(
                    'relative mt-4 max-w-[calc(100svw-64px)] overflow-x-auto rounded-sm bg-muted px-4 py-3 font-mono text-sm',
                    className
                )}
                data-language={language}
                {...props}
            >
                {children}
            </pre>

            <Button
                variant="ghost"
                size="sm"
                className="absolute right-2 top-2 h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity bg-background/80 backdrop-blur-sm"
                onClick={handleCopy}
                type="button"
            >
                {copied ? (
                    <>
                        <CheckIcon className="mr-1 h-3 w-3" />
                        Copied
                    </>
                ) : (
                    <>
                        <CopyIcon className="mr-1 h-3 w-3" />
                        Copy
                    </>
                )}
            </Button>
        </div>
    );
}